// Profiles: the card shown when you click someone, your photo and details, your status message and Do not disturb.
import { createContext, useContext, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { APP_NAME, ApiError, api } from "../../lib/api";
import { startCall, type CallMedia, type CallNavState } from "../../lib/calls";
import { STATUS_EMOJIS, activeStatus, isDnd, untilText, useNow, usePerson } from "../../lib/people";
import { Alert, Avatar, Button, Card, Dialog, Field, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { PresenceDot, usePresence } from "../chat/dialogs";
import { useOrg } from "../org";
import { useMe } from "../../lib/session";

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Please try again.");
const refreshMe = (qc: ReturnType<typeof useQueryClient>, slug: string) => Promise.all([qc.invalidateQueries({ queryKey: ["me"] }), qc.invalidateQueries({ queryKey: ["people", slug] })]);

// ---------------------------------------------------------------- the profile card

const CardContext = createContext<(userId: string) => void>(() => {});
export const useProfileCard = () => useContext(CardContext);

export function ProfileCardProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);
  return (
    <CardContext.Provider value={setUserId}>
      {children}
      {userId && <ProfileCard userId={userId} onClose={() => setUserId(null)} />}
    </CardContext.Provider>
  );
}

// A name or photo that opens the person's card.
export function PersonButton({ userId, children, className, label }: { userId: string; children: ReactNode; className?: string; label: string }) {
  const open = useProfileCard();
  return <button type="button" onClick={() => open(userId)} className={cx("rounded-full text-left", className)} aria-label={`${label}'s profile`}>{children}</button>;
}

function ProfileCard({ userId, onClose }: { userId: string; onClose: () => void }) {
  const { org, me } = useOrg();
  const person = usePerson(userId);
  const presence = usePresence()[userId];
  const now = useNow();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<"message" | CallMedia | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!person) return null;
  const status = activeStatus(person.status, now);
  const dnd = isDnd(person.dndUntil, now) && presence !== "offline";
  const presenceText = dnd ? `Do not disturb ${untilText(person.dndUntil!, "")}`.trim() : presence === "online" ? "Online" : presence === "away" ? "Away" : presence === "busy" ? "In a call" : "Offline";
  const isMe = userId === me.user.id;

  async function conversationId() {
    return (await api<{ conversation: { id: string } }>(`/orgs/${org.slug}/dms`, { method: "POST", body: { userId } })).conversation.id;
  }
  async function message() {
    setBusy("message"); setError(null);
    try { const id = await conversationId(); onClose(); navigate(`/o/${org.slug}/chat/${id}`); } catch (err) { setError(errorText(err)); setBusy(null); }
  }
  async function call(media: CallMedia) {
    setBusy(media); setError(null);
    try {
      const r = await startCall(org.slug, await conversationId(), media);
      onClose();
      navigate(`/o/${org.slug}/call/${r.call.id}`, { state: { join: r.join, call: r.call, media } satisfies CallNavState });
    } catch (err) { setError(errorText(err)); setBusy(null); }
  }

  return (
    <Dialog open onClose={onClose} title="Profile">
      <div className="flex flex-col items-center text-center">
        <span className="relative">
          <Avatar name={person.name} userId={userId} size="xl" />
          <PresenceDot status={presence} userId={userId} className="absolute bottom-1 right-1 h-4 w-4 ring-4" />
        </span>
        <h3 className="mt-3 text-xl font-semibold">{person.name}{isMe && <span className="font-normal text-slate-500"> (you)</span>}</h3>
        {person.jobTitle && <p className="text-sm text-slate-600 dark:text-slate-300">{person.jobTitle}</p>}
        <p className={cx("mt-1 text-sm", dnd ? "font-medium text-red-600 dark:text-red-400" : "text-slate-500")}>{presenceText}</p>
        {status && (
          <p className="mt-3 max-w-full rounded-lg bg-slate-100 px-3 py-2 text-sm dark:bg-slate-800">
            {status.emoji && <span className="mr-1.5" aria-hidden="true">{status.emoji}</span>}{status.text}
            {status.expiresAt && <span className="block text-xs text-slate-500">Clears {untilText(status.expiresAt).replace(/^until /, "")}</span>}
          </p>
        )}
      </div>
      {error && <div className="mt-4"><Alert>{error}</Alert></div>}
      {isMe ? (
        <div className="mt-5 flex justify-center"><Link to={`/o/${org.slug}/account`} onClick={onClose} className="text-sm font-medium text-brand-600 hover:underline">Edit your profile</Link></div>
      ) : (
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <Button onClick={message} loading={busy === "message"} disabled={busy !== null}><Icon name="chat" className="h-4 w-4" />Message</Button>
          <Button variant="secondary" onClick={() => call("audio")} loading={busy === "audio"} disabled={busy !== null}><Icon name="phone" className="h-4 w-4" />Audio call</Button>
          <Button variant="secondary" onClick={() => call("video")} loading={busy === "video"} disabled={busy !== null}><Icon name="video" className="h-4 w-4" />Video call</Button>
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------- your status and Do not disturb (account menu)

const endOfToday = () => { const d = new Date(); d.setHours(23, 59, 59, 0); return d; };
const startOfNextWeek = () => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + (((8 - d.getDay()) % 7) || 7)); return d; };
const tomorrowAt9 = () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; };
const inMinutes = (m: number) => new Date(Date.now() + m * 60_000);

const CLEAR_AFTER: { id: string; label: string; at: () => Date | null }[] = [
  { id: "never", label: "Don't clear", at: () => null },
  { id: "30m", label: "30 minutes", at: () => inMinutes(30) },
  { id: "1h", label: "1 hour", at: () => inMinutes(60) },
  { id: "4h", label: "4 hours", at: () => inMinutes(240) },
  { id: "today", label: "Today", at: endOfToday },
  { id: "week", label: "This week", at: startOfNextWeek },
];
const DND_FOR: { label: string; at: () => Date | null }[] = [
  { label: "For 30 minutes", at: () => inMinutes(30) },
  { label: "For 1 hour", at: () => inMinutes(60) },
  { label: "For 2 hours", at: () => inMinutes(120) },
  { label: "Until tomorrow 9:00", at: tomorrowAt9 },
  { label: "Until I turn it off", at: () => null },
];

// The part of the account menu about you right now: your status message and Do not disturb.
export function YourStatus({ onChanged }: { onChanged?: () => void }) {
  const { me, org } = useOrg();
  const qc = useQueryClient();
  const now = useNow();
  const [editing, setEditing] = useState(false);
  const [showDnd, setShowDnd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = activeStatus(me.user.status, now);
  const dnd = isDnd(me.user.dndUntil, now);
  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try { await fn(); await refreshMe(qc, org.slug); onChanged?.(); } catch (err) { setError(errorText(err)); }
  }
  const item = "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800";
  return (
    <>
      <button type="button" role="menuitem" onClick={() => setEditing(true)} className={item}>
        {status ? <><span aria-hidden="true">{status.emoji ?? "💬"}</span><span className="min-w-0 flex-1 truncate">{status.text || "Status set"}</span></> : <><Icon name="smile" className="h-4 w-4" />Set a status message</>}
      </button>
      {dnd ? (
        <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm">
          <span className="grid h-3 w-3 place-items-center rounded-full bg-red-500"><span className="h-[2px] w-1.5 rounded bg-white" /></span>
          <span className="min-w-0 flex-1"><span className="font-medium">Do not disturb</span><span className="block text-xs text-slate-500">{untilText(me.user.dndUntil!)}</span></span>
          <button type="button" role="menuitem" onClick={() => run(() => api("/me/dnd", { method: "DELETE" }))} className="rounded-md px-2 py-1 text-xs font-medium text-brand-600 hover:bg-slate-100 dark:hover:bg-slate-800">Turn off</button>
        </div>
      ) : (
        <>
          <button type="button" role="menuitem" aria-expanded={showDnd} onClick={() => setShowDnd((s) => !s)} className={item}>
            <span className="grid h-4 w-4 place-items-center"><span className="grid h-3 w-3 place-items-center rounded-full bg-red-500"><span className="h-[2px] w-1.5 rounded bg-white" /></span></span>
            <span className="flex-1">Do not disturb</span><Icon name="chevron" className={cx("h-4 w-4 transition-transform", showDnd && "rotate-180")} />
          </button>
          {showDnd && (
            <div className="ml-6 border-l border-slate-200 pl-1 dark:border-slate-700">
              {DND_FOR.map((o) => (
                <button key={o.label} type="button" role="menuitem" onClick={() => run(() => api("/me/dnd", { method: "PUT", body: { until: o.at()?.toISOString() ?? null } }))} className={item}>{o.label}</button>
              ))}
            </div>
          )}
        </>
      )}
      {error && <p role="alert" className="px-3 py-1 text-xs text-red-600">{error}</p>}
      {editing && <StatusDialog onClose={() => setEditing(false)} onSaved={() => { void refreshMe(qc, org.slug); onChanged?.(); }} />}
    </>
  );
}

function StatusDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { me } = useOrg();
  const current = activeStatus(me.user.status);
  const [emoji, setEmoji] = useState<string | null>(current?.emoji ?? null);
  const [text, setText] = useState(current?.text ?? "");
  const [clearAfter, setClearAfter] = useState("today");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const at = CLEAR_AFTER.find((c) => c.id === clearAfter)!.at();
      await api("/me/status", { method: "PUT", body: { emoji, text, expiresAt: at?.toISOString() ?? null } });
      onSaved(); onClose();
    } catch (err) { setError(errorText(err)); setBusy(false); }
  }
  async function clear() {
    setBusy(true); setError(null);
    try { await api("/me/status", { method: "DELETE" }); onSaved(); onClose(); } catch (err) { setError(errorText(err)); setBusy(false); }
  }
  return (
    <Dialog open onClose={onClose} title="Your status">
      <form onSubmit={save} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <fieldset>
          <legend className="mb-2 text-sm font-medium">Emoji</legend>
          <div className="grid grid-cols-6 gap-1 sm:grid-cols-8">
            {STATUS_EMOJIS.map((e) => (
              <button key={e} type="button" onClick={() => setEmoji((x) => (x === e ? null : e))} aria-pressed={emoji === e} aria-label={`Status emoji ${e}`}
                className={cx("rounded-lg p-1.5 text-xl ring-2", emoji === e ? "bg-brand-50 ring-brand-500 dark:bg-brand-700/30" : "ring-transparent hover:bg-slate-100 dark:hover:bg-slate-800")}>{e}</button>
            ))}
          </div>
        </fieldset>
        <Field label="What's happening?" name="text" value={text} onChange={(e) => setText(e.currentTarget.value)} maxLength={100} placeholder="In a workshop until 3, On holiday…" />
        <label className="block text-sm font-medium">Clear after
          <select value={clearAfter} onChange={(e) => setClearAfter(e.target.value)} className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
            {CLEAR_AFTER.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </label>
        <div className="flex justify-between gap-2">
          <Button type="button" variant="ghost" onClick={clear} disabled={busy || !current}>Clear status</Button>
          <Button type="submit" loading={busy} disabled={!text.trim() && !emoji}>Save</Button>
        </div>
      </form>
    </Dialog>
  );
}

// A reminder in the top bar while Do not disturb is on (one click turns it off).
export function DndChip() {
  const { me, org } = useOrg();
  const qc = useQueryClient();
  const now = useNow();
  if (!isDnd(me.user.dndUntil, now)) return null;
  async function off() { await api("/me/dnd", { method: "DELETE" }).catch(() => undefined); await refreshMe(qc, org.slug); }
  return (
    <button type="button" onClick={off} title="Turn off Do not disturb" className="flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 dark:bg-red-950/50 dark:text-red-200">
      <span className="grid h-3 w-3 place-items-center rounded-full bg-red-500"><span className="h-[2px] w-1.5 rounded bg-white" /></span>
      <span className="hidden sm:inline">Do not disturb · {untilText(me.user.dndUntil!)}</span><span className="sm:hidden">DND</span>
    </button>
  );
}

// ---------------------------------------------------------------- your photo and details (account page)

// The middle square of the picture, 256 × 256, as WebP (or JPEG where WebP can't be made). Drawing it fresh also drops
// the camera's metadata (such as where the photo was taken).
async function squarePhoto(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 256;
  const g = canvas.getContext("2d")!;
  g.imageSmoothingQuality = "high";
  g.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, 256, 256);
  bitmap.close();
  const as = (type: string, q: number) => new Promise<Blob | null>((r) => canvas.toBlob(r, type, q));
  const webp = await as("image/webp", 0.88);
  const blob = webp?.type === "image/webp" ? webp : await as("image/jpeg", 0.9);
  if (!blob) throw new Error("no image");
  return blob;
}

export function ProfileEditor() {
  const { me, org } = useOrg();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  async function choose(file: File | undefined) {
    if (!file) return;
    setPhotoBusy(true); setMsg(null);
    try {
      let blob: Blob;
      try { blob = await squarePhoto(file); } catch { throw new ApiError(415, "BAD_IMAGE", "That picture couldn't be opened. Try a JPEG or PNG."); }
      const form = new FormData();
      form.append("file", blob, blob.type === "image/webp" ? "photo.webp" : "photo.jpg");
      const res = await fetch("/api/v1/me/avatar", { method: "POST", body: form, credentials: "same-origin" });
      if (!res.ok) {
        const e = (await res.json().catch(() => null))?.error ?? {};
        throw new ApiError(res.status, e.code ?? "ERROR", e.message ?? "The photo couldn't be saved.");
      }
      await refreshMe(qc, org.slug);
      setMsg({ kind: "success", text: "Photo updated." });
    } catch (err) { setMsg({ kind: "error", text: errorText(err) }); } finally {
      setPhotoBusy(false);
      if (input.current) input.current.value = "";
    }
  }
  async function removePhoto() {
    setPhotoBusy(true); setMsg(null);
    try { await api("/me/avatar", { method: "DELETE" }); await refreshMe(qc, org.slug); } catch (err) { setMsg({ kind: "error", text: errorText(err) }); } finally { setPhotoBusy(false); }
  }
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    setBusy(true); setMsg(null);
    try {
      await api("/me/profile", { method: "PATCH", body: { name: String(data.get("name")), jobTitle: String(data.get("jobTitle")) } });
      await refreshMe(qc, org.slug);
      setMsg({ kind: "success", text: "Saved." });
    } catch (err) { setMsg({ kind: "error", text: errorText(err) }); } finally { setBusy(false); }
  }

  return (
    <Card>
      <h2 className="mb-4 font-semibold">Your profile</h2>
      {msg && <div className="mb-4"><Alert kind={msg.kind}>{msg.text}</Alert></div>}
      <div className="flex flex-wrap items-start gap-6">
        <div className="flex flex-col items-center gap-2">
          <span className="relative">
            <Avatar name={me.user.name} src={me.user.avatarUrl} size="xl" />
            {photoBusy && <span className="absolute inset-0 grid place-items-center rounded-full bg-black/40 text-white"><Spinner /></span>}
          </span>
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif" className="sr-only" aria-label="Choose a photo" onChange={(e) => choose(e.currentTarget.files?.[0])} />
          <Button size="sm" variant="secondary" onClick={() => input.current?.click()} disabled={photoBusy}>{me.user.avatarUrl ? "Change photo" : "Add a photo"}</Button>
          {me.user.avatarUrl && <button type="button" onClick={removePhoto} disabled={photoBusy} className="text-xs text-red-600 hover:underline">Remove photo</button>}
        </div>
        <form onSubmit={save} className="min-w-60 flex-1 space-y-4">
          <Field label="Name" name="name" defaultValue={me.user.name} required maxLength={80} />
          <Field label="Job title" name="jobTitle" defaultValue={me.user.jobTitle} maxLength={80} placeholder="Product designer" hint="Shown to people in your organizations." />
          <div className="flex justify-end"><Button type="submit" loading={busy}>Save</Button></div>
        </form>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- two-factor sign-in (account page)

type Step = { kind: "off" } | { kind: "scan"; secret: string; qr: string } | { kind: "codes"; codes: string[] } | { kind: "on" } | { kind: "confirm"; action: "disable" | "codes" };

// Works inside an organization (account page) and outside it (the admin panel asks admins to turn it on).
export function TwoFactorCard() {
  const me = useMe().data!;
  const qc = useQueryClient();
  const on = me.user.mfa.enabled;
  const [step, setStep] = useState<Step>({ kind: on ? "on" : "off" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shown = step.kind === "codes" || step.kind === "scan" || step.kind === "confirm" ? step : { kind: on ? "on" as const : "off" as const };

  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true); setError(null);
    try { return await fn(); } catch (err) { setError(errorText(err)); return undefined; } finally { setBusy(false); }
  }
  async function start() {
    const r = await run(() => api<{ secret: string; otpauthUrl: string }>("/me/mfa/setup", { method: "POST" }));
    if (!r) return;
    const QR = await import("qrcode");
    setStep({ kind: "scan", secret: r.secret, qr: await QR.toDataURL(r.otpauthUrl, { margin: 1, width: 200 }) });
  }
  async function confirmCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const code = String(new FormData(e.currentTarget).get("code") ?? "");
    if (shown.kind === "scan") {
      const r = await run(() => api<{ recoveryCodes: string[] }>("/me/mfa/enable", { method: "POST", body: { code } }));
      if (r) { setStep({ kind: "codes", codes: r.recoveryCodes }); void qc.invalidateQueries({ queryKey: ["me"] }); }
    } else if (shown.kind === "confirm" && shown.action === "codes") {
      const r = await run(() => api<{ recoveryCodes: string[] }>("/me/mfa/recovery-codes", { method: "POST", body: { code } }));
      if (r) { setStep({ kind: "codes", codes: r.recoveryCodes }); void qc.invalidateQueries({ queryKey: ["me"] }); }
    } else if (shown.kind === "confirm" && shown.action === "disable") {
      const r = await run(() => api("/me/mfa/disable", { method: "POST", body: { code } }));
      if (r) { setStep({ kind: "off" }); void qc.invalidateQueries({ queryKey: ["me"] }); }
    }
  }
  function download(codes: string[]) {
    const text = `${APP_NAME} recovery codes for ${me.user.email}\nEach code works once. Keep them somewhere safe.\n\n${codes.join("\n")}\n`;
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `${APP_NAME.toLowerCase()}-recovery-codes.txt` });
    a.click(); URL.revokeObjectURL(url);
  }
  const codeField = <Field label="Code from your app" name="code" required autoFocus autoComplete="one-time-code" inputMode="numeric" maxLength={11} placeholder="123456" className="min-w-48 flex-1" />;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Two-factor sign-in</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">A code from an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…) as well as your password or sign-in link.</p>
        </div>
        <span className={cx("rounded-full px-2.5 py-1 text-xs font-semibold", on ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300")}>{on ? "On" : "Off"}</span>
      </div>
      {error && <div className="mt-4"><Alert>{error}</Alert></div>}

      {shown.kind === "off" && <Button className="mt-4" onClick={start} loading={busy}>Turn on two-factor sign-in</Button>}

      {shown.kind === "scan" && (
        <div className="mt-4 flex flex-wrap gap-6">
          <img src={shown.qr} alt="QR code to scan with your authenticator app" className="h-48 w-48 rounded-lg bg-white p-1 ring-1 ring-slate-200" />
          <form onSubmit={confirmCode} className="min-w-60 flex-1 space-y-3">
            <p className="text-sm">1. Scan the code with your authenticator app. Can't scan? Enter this key instead:</p>
            <code className="block select-all break-all rounded-lg bg-slate-100 px-3 py-2 font-mono text-sm tracking-wider dark:bg-slate-800" aria-label="Set-up key">{shown.secret.match(/.{1,4}/g)!.join(" ")}</code>
            <p className="text-sm">2. Enter the 6-digit code the app shows.</p>
            <div className="flex flex-wrap items-end gap-2">{codeField}<Button type="submit" loading={busy}>Turn on</Button></div>
            <button type="button" onClick={() => setStep({ kind: "off" })} className="text-sm text-slate-500 hover:underline">Cancel</button>
          </form>
        </div>
      )}

      {shown.kind === "codes" && (
        <div className="mt-4 space-y-3">
          <Alert kind="info">Save these recovery codes now - they won't be shown again. If you lose your phone, each one lets you sign in once.</Alert>
          <ul className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-3 font-mono text-sm sm:grid-cols-5 dark:bg-slate-800" aria-label="Recovery codes">
            {shown.codes.map((c) => <li key={c} className="select-all">{c}</li>)}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(shown.codes.join("\n"))}>Copy</Button>
            <Button variant="secondary" onClick={() => download(shown.codes)}>Download</Button>
            <Button onClick={() => setStep({ kind: "on" })}>I've saved them</Button>
          </div>
        </div>
      )}

      {shown.kind === "on" && (
        <div className="mt-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            On since {me.user.mfa.enabledAt ? new Date(me.user.mfa.enabledAt).toLocaleDateString() : "recently"} · {me.user.mfa.recoveryCodesLeft} recovery {me.user.mfa.recoveryCodesLeft === 1 ? "code" : "codes"} left
            {me.user.mfa.recoveryCodesLeft <= 3 && <span className="font-medium text-amber-700 dark:text-amber-300"> - make new ones soon</span>}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setStep({ kind: "confirm", action: "codes" })}>New recovery codes</Button>
            <Button variant="ghost" className="text-red-600" onClick={() => setStep({ kind: "confirm", action: "disable" })}>Turn off</Button>
          </div>
        </div>
      )}

      {shown.kind === "confirm" && (
        <form onSubmit={confirmCode} className="mt-4 space-y-3">
          <p className="text-sm">{shown.action === "disable" ? "To turn off two-factor sign-in, enter a code from your app (or a recovery code)." : "To make new recovery codes (the old ones stop working), enter a code from your app."}</p>
          <div className="flex flex-wrap items-end gap-2">{codeField}<Button type="submit" variant={shown.action === "disable" ? "danger" : "primary"} loading={busy}>{shown.action === "disable" ? "Turn off" : "Make new codes"}</Button></div>
          <button type="button" onClick={() => setStep({ kind: "on" })} className="text-sm text-slate-500 hover:underline">Cancel</button>
        </form>
      )}
    </Card>
  );
}
